import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import {
  COMPANY_PAYROLL__TOTAL_HOURS_DECIMALS_ERROR,
  COMPANY_PAYROLL__TOTAL_HOURS_MAX_ERROR,
  COMPANY_PAYROLL__TOTAL_HOURS_MIN_ERROR,
} from 'translations/constants';
import { INightShift, IOvertimeLimits, IServiceCode, IWorkhourSettings } from 'types/wage-calculation';
import { OvertimeLimitsFields, WorkhourSettingsFields } from './types';

const isValidCode = (code: string) => /^\d{4,6}$/.test(code);

// const isValidNumber = (value: string) => /^\d{1,3}$/.test(value);
const isValidNumber = (value: string) => /^\d{1,3}(\.\d+)?$/.test(value);

const onChangeValue = (
  event: ChangeEvent<HTMLInputElement>,
  setValue:
    | Dispatch<SetStateAction<INightShift>>
    | Dispatch<SetStateAction<IServiceCode>>
    | Dispatch<SetStateAction<IOvertimeLimits>>
    | Dispatch<SetStateAction<IWorkhourSettings>>,
  validationFunction: (value: string) => boolean,
  field: OvertimeLimitsFields | WorkhourSettingsFields | 'code',
  setValidValues: Dispatch<SetStateAction<boolean>>,
  setShouldUpdate: Dispatch<SetStateAction<boolean>>,
) => {
  const { value } = event.target;

  if (Number.isNaN(+value)) return;

  setValidValues(validationFunction(value));

  setValue(prevState => ({
    ...prevState,
    [field]: value,
  }));

  setShouldUpdate(true);
};

export const onChangeCode = (
  event: ChangeEvent<HTMLInputElement>,
  setCode: Dispatch<SetStateAction<INightShift>> | Dispatch<SetStateAction<IServiceCode>>,
  setValidValues: Dispatch<SetStateAction<boolean>>,
  setShouldUpdate: Dispatch<SetStateAction<boolean>>,
) => {
  onChangeValue(event, setCode, isValidCode, 'code', setValidValues, setShouldUpdate);
};

export const onChangeHourValue = (
  event: ChangeEvent<HTMLInputElement>,
  setHourValue: Dispatch<SetStateAction<IOvertimeLimits>> | Dispatch<SetStateAction<IWorkhourSettings>>,
  field: OvertimeLimitsFields | WorkhourSettingsFields,
  setValidValues: Dispatch<SetStateAction<boolean>>,
  setShouldUpdate: Dispatch<SetStateAction<boolean>>,
) => {
  onChangeValue(event, setHourValue, isValidNumber, field, setValidValues, setShouldUpdate);
};

export const WORKING_SYSTEM_DAYS = [1, 2, 3, 4, 5, 6, 7];
const TOTAL_HOURS_MAX = 168; // 24 x 7

// lets the user type a (negative) number with a dot or comma, so the validation messages can be shown
export const isTotalHoursInput = (value: string) => /^-?\d*([.,]\d*)?$/.test(value);

export const parseHours = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isNaN(parsed) ? null : parsed;
};

// e.g. 38 -> "38,00", the way Total hours is shown once loaded
export const formatTotalHours = (value: string | number | null | undefined): string | null => {
  const hours = parseHours(value);
  return hours === null ? null : hours.toFixed(2).replace('.', ',');
};

export const validateTotalHours = (
  value: string | number | null | undefined,
  allowEmpty: boolean,
): { isValid: boolean; errorKey: string | null } => {
  const raw = value === null || value === undefined ? '' : String(value).trim();
  if (raw === '') return { isValid: allowEmpty, errorKey: null };

  const hours = parseHours(raw);
  if (hours === null) return { isValid: false, errorKey: null };
  if (hours < 0) return { isValid: false, errorKey: COMPANY_PAYROLL__TOTAL_HOURS_MIN_ERROR };
  if (!/^\d*([.,]\d{0,2})?$/.test(raw))
    return { isValid: false, errorKey: COMPANY_PAYROLL__TOTAL_HOURS_DECIMALS_ERROR };
  if (hours > TOTAL_HOURS_MAX) return { isValid: false, errorKey: COMPANY_PAYROLL__TOTAL_HOURS_MAX_ERROR };

  return { isValid: true, errorKey: null };
};

/**
 * Hours per day = total hours per week / working system, rounded to the nearest whole minute.
 * Returns null when not configured. Calculated in hundredths of an hour to round like the API.
 */
export const calculateHoursPerDayMinutes = (
  workingSystem: number | null | undefined,
  totalHours: string | number | null | undefined,
): number | null => {
  const hours = parseHours(totalHours);
  if (!workingSystem || workingSystem < 1 || workingSystem > 7 || hours === null || hours <= 0) return null;

  return Math.round((Math.round(hours * 100) * 60) / (100 * workingSystem));
};

// e.g. 456 minutes -> "7,60 h (7:36)"
export const formatHoursPerDay = (minutes: number) => {
  const decimalHours = (minutes / 60).toFixed(2).replace('.', ',');
  const clockTime = `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;

  return `${decimalHours} h (${clockTime})`;
};
